import React, { Fragment, memo } from "react";
import clsx from "clsx";
import camelCase from "lodash/camelCase";
import Text from "../../../../../../shared-components/css-modules/toyota/atoms/Text";
import { CarConfigConfigureSection } from "../../../../../utils/types/SectionTypes";
import type { SectionWrapperPropagationProps } from "../SectionWrapper";
import SectionWrapper from "../SectionWrapper";
import toggle from "../../../../../../shared-components/css-modules/toyota/organisms/Switch.module.scss";
import styles from "./Charging.module.scss";
import ChargingPublic from "./ChargingPublic";
import ChargingAtHome from "./ChargingAtHome";
import { useCarConfigChargerTabs } from "../../../../../hooks/useCarConfigChargers";
import { useCarConfigSelector } from "../../../../../redux/store";
import { getAemImageUrl } from "../../../../../../../../common-deprecated/utils";
import { CarConfigLabel, useCarConfigLabel } from "../../../../../utils/labelUtils";
import type { ChargingType } from "../../../../../../../../common-deprecated/types/SettingsType";
import type { ExtraType } from "../../../../../../../../common-deprecated/types/CommonTypes";

const ChargingComponent: React.FC<{
    tab: ChargingType | null;
    homechargers: ExtraType[];
    setActiveTab: (tab: ChargingType) => void;
}> = ({ tab, homechargers, setActiveTab }) => {
    if (!tab) return null;

    switch (tab.type) {
        case "public":
            return (
                <ChargingPublic
                    chargingAceData={tab}
                    changeTabFunction={() => setActiveTab(tab)}
                    hasHomeChargers={!!homechargers.length}
                />
            );
        case "home":
            return <ChargingAtHome chargingAceData={tab} homechargers={homechargers} />;
        default:
            return null;
    }
};

const Charging: React.FC<SectionWrapperPropagationProps> = ({ sectionRefs }) => {
    const chargingSectionIsActive = useCarConfigSelector(
        (state) => state.carConfigNavigation.activeSection === CarConfigConfigureSection.Charging,
    );
    const resourcePath = useCarConfigSelector((state) => state.commonSettings.resourcePath);
    const [navigationLabel, publicChargingLabel, homeChargingLabel] = useCarConfigLabel([
        "ariaChargingNavigation",
        "carConfigPublicCharging",
        "carConfigChargingAtHome",
    ]);
    const { aceData, homechargers, activeTab, setActiveTab } = useCarConfigChargerTabs();
    // Looks like scl=1 always results in external errors, using scl=2 instead
    const imageUrl =
        activeTab?.type === "public" && activeTab?.bannerLinkImage
            ? getAemImageUrl(activeTab?.bannerLinkImage, { scl: 2 })
            : `${resourcePath}/toyota/img/apheleia/charging/background.jpg`;

    const getKey = (tab: ChargingType): string => {
        const { type, title } = tab;
        const formattedTitle = camelCase(title || (type === "public" ? publicChargingLabel : homeChargingLabel));
        return `${type as string}-${formattedTitle}`;
    };

    return (
        <SectionWrapper
            sectionId={CarConfigConfigureSection.Charging}
            sectionRef={sectionRefs.get(CarConfigConfigureSection.Charging)}
            hasToggle
        >
            <div
                className={clsx(
                    styles["charging-visual"],
                    chargingSectionIsActive && activeTab && activeTab.type !== "home" && styles.show,
                )}
            >
                <div
                    className={styles["charging-image"]}
                    style={{
                        backgroundImage: `url(${imageUrl})`,
                        backgroundSize: "cover",
                    }}
                />
            </div>

            <Text variant="apheleia-h2" tag="h2" spacing="spacing-4">
                <CarConfigLabel label="carConfigChargingOptions" />
            </Text>

            <nav
                className={clsx(styles.toggle, toggle.switch, (aceData || []).length <= 1 && styles["only-one-toggle"])}
                aria-label={navigationLabel}
            >
                {aceData?.map((tab, index) => (
                    <Fragment key={`charging-toggle-${getKey(tab)}`}>
                        <input
                            id={`charging-toggle-${getKey(tab)}`}
                            className={`${toggle.toggle} ${index % 2 ? toggle.right : toggle.left}`}
                            name="toggleCharging"
                            value={index === 0 ? "true" : "false"}
                            type="radio"
                            onChange={() => {
                                setActiveTab(tab);
                            }}
                            checked={tab === activeTab}
                        />
                        <label htmlFor={`charging-toggle-${getKey(tab)}`} className={toggle.option}>
                            {tab.title || (tab.type === "public" ? publicChargingLabel : homeChargingLabel)}
                        </label>
                    </Fragment>
                ))}
                {!aceData && homechargers.length > 0 && (
                    <>
                        <input
                            id="charging-toggle-home"
                            className={`${toggle.toggle} ${toggle.left}`}
                            name="toggleCharging"
                            value="true"
                            type="radio"
                            checked
                            readOnly
                        />
                        <label htmlFor="charging-toggle-home" className={toggle.option}>
                            {homeChargingLabel}
                        </label>
                    </>
                )}
            </nav>
            <div>
                {aceData?.map((tab) => (
                    <div
                        className={clsx(styles["charging-section"], activeTab === tab ? styles.active : "")}
                        key={`charging-content-${getKey(tab)}`}
                    >
                        {ChargingComponent({ homechargers, tab, setActiveTab })}
                    </div>
                ))}
                {!aceData && homechargers.length > 0 && (
                    <div className={clsx(styles["charging-section"], styles.active)} key="charging-content-home">
                        <ChargingAtHome chargingAceData={null} homechargers={homechargers} />
                    </div>
                )}
            </div>
        </SectionWrapper>
    );
};

export default memo(Charging);
